import { fabric } from 'fabric';
import { TextListTypeEnum, KeyCodeTypeEnum } from 'enums';
import { instance } from 'features/workspace/canvasSlice';

function extendMainObject() {
  fabric.Object.prototype.handleRotation = function () {
    if (Math.round(this.get('angle')) % 45 <= 3 || Math.round(this.get('angle')) % 45 >= 42) {
      this.straightenCustom();

      this.lockRotation = true;

      setTimeout(() => {
        this.lockRotation = false;
      }, 100);
    }
  };

  fabric.Object.prototype.straightenCustom = function () {
    let currentAngle = this.get('angle') % 360;
    let newAngle = 0;

    if (currentAngle > 0) {
      newAngle = Math.round((currentAngle - 1) / 45) * 45;
    } else {
      newAngle = Math.round(currentAngle / 45) * 45;
    }

    this.rotate(newAngle);
  };

  fabric.Object.prototype.set({
    objectCaching: true,
    lockScalingFlip: true,
    globalCompositeOperation: 'source-over',
    borderColor: '#5FA6A0',
    cornerColor: 'rgba(95,166,160,.7)',
    cornerSize: 12,
    cornerStyle: 'circle',
    transparentCorners: false,
  });

  fabric.Object.prototype.getAngleInRadians = function () {
    return (this.get('angle') / 180) * Math.PI;
  };

  fabric.Canvas.prototype.getAbsoluteCoords = function (object) {
    return {
      left: object.left + this._offset.left,
      top: object.top + this._offset.top,
    };
  };

  fabric.Canvas.prototype.getAbsoluteCenter = function (object) {
    return {
      left: object.getCenterPoint().x * instance.getZoom() + this._offset.left,
      top: object.getCenterPoint().y * instance.getZoom() + this._offset.top,
    };
  };

  // Fix Fabric.js v2.0 breaking changes
  fabric.Image.fromObject = function (object, callback) {
    fabric.util.loadImage(
      object.src,
      function (img, error) {
        if (error) {
          callback && callback(null, error);
          return;
        }
        fabric.Image.prototype._initFilters.call(object, object.filters, function (filters) {
          object.filters = filters || [];
          fabric.Image.prototype._initFilters.call(
            object,
            [object.resizeFilter],
            function (resizeFilters) {
              object.resizeFilter = resizeFilters[0];
              if (typeof object.version === 'undefined') {
                var elWidth = img.naturalWidth || img.width;
                var elHeight = img.naturalHeight || img.height;
                var scaleX = ((object.scaleX || 1) * object.width) / elWidth;
                var scaleY = ((object.scaleY || 1) * object.height) / elHeight;
                object.width = elWidth;
                object.height = elHeight;
                object.scaleX = scaleX;
                object.scaleY = scaleY;
              }
              var image = new fabric.Image(img, object);
              callback(image);
            },
          );
        });
      },
      null,
      object.crossOrigin,
    );
  };
  fabric.PathGroup = {};
  fabric.PathGroup.fromObject = function (object, callback) {
    var originalPaths = object.paths;
    delete object.paths;
    if (typeof originalPaths === 'string') {
      fabric.loadSVGFromURL(originalPaths, function (elements) {
        var pathUrl = originalPaths;
        var group = fabric.util.groupSVGElements(elements, object, pathUrl);
        group.type = 'group';
        object.paths = originalPaths;
        callback(group);
      });
    } else {
      fabric.util.enlivenObjects(originalPaths, function (enlivenedObjects) {
        enlivenedObjects.forEach(function (obj) {
          obj._removeTransformMatrix();
        });
        var group = new fabric.Group(enlivenedObjects, object);
        group.type = 'group';
        object.paths = originalPaths;
        callback(group);
      });
    }
  };
  fabric.Image.prototype.toObject = (function (toObject) {
    return function () {
      return fabric.util.object.extend(toObject.call(this), {
        imgType: this.imgType,
        imgUrl: this.imgUrl,
        imgId: this.imgId,
        hasColor: this.hasColor,
        tags: this.tags,
        clipPath: this.clipPath,
      });
    };
  })(fabric.Image.prototype.toObject);
}

function setupTextObject() {
  fabric.Textbox.prototype.toObject = (function (toObject) {
    return function () {
      return fabric.util.object.extend(toObject.call(this), {
        fill: this.fill,
        text: this.text,
        fontWeight: this.fontWeight,
        fontFamily: this.fontFamily,
        fontSize: this.fontSize,
        textAlign: this.textAlign,
        fontStyle: this.fontStyle,
        textDecoration: this.textDecoration,
        styles: fabric.util.object.clone(this.styles, true),
        lineHeight: this.lineHeight,
      });
    };
  })(fabric.Textbox.prototype.toObject);

  fabric.Textbox.prototype.onKeyDown = (function (onKeyDown) {
    return function (e) {
      onKeyDown.call(this, e);

      setTimeout(() => {
        // Helper functions
        const hardLines = () => this.text.split('\n');
        const lineStartIndex = (lines, idx) => {
          let sum = 0;
          for (let i = 0; i < idx; i++) sum += lines[i].length + 1;
          return sum;
        };
        const selectionToHardPos = () => {
          const sel = this.selectionStart || 0;
          const before = this.text.slice(0, sel);
          const lineIdx = before.split('\n').length - 1;
          const start = before.lastIndexOf('\n') === -1 ? 0 : before.lastIndexOf('\n') + 1;
          const charIdx = sel - start;
          return { lineIdx, charIdx };
        };

        // Convert number to letter (1->a, 2->b, etc.)
        const numberToLetter = (num) => {
          return String.fromCharCode(96 + num); // 97 is 'a'
        };

        // Convert number to Roman numeral
        const numberToRoman = (num) => {
          const romanNumerals = [
            ['x', 10],
            ['ix', 9],
            ['v', 5],
            ['iv', 4],
            ['i', 1],
          ];
          let result = '';
          for (const [roman, value] of romanNumerals) {
            while (num >= value) {
              result += roman;
              num -= value;
            }
          }
          return result;
        };

        // Detect indent level (0, 4, 8 spaces = levels 0, 1, 2)
        const getIndentLevel = (line) => {
          const match = line.match(/^(\s*)/);
          const spaces = match ? match[1].length : 0;
          return Math.floor(spaces / 4);
        };

        // Get bullet symbol based on indent level
        const getBulletSymbol = (level) => {
          if (level === 0) {
            return '•'; // Filled circle
          } else if (level === 1) {
            return '◦'; // Hollow circle
          } else if (level === 2) {
            return '▪'; // Small square
          } else {
            return '•'; // Fall back to filled circle for deeper levels
          }
        };

        // Function to renumber all lines maintaining hierarchy - DEFINED BEFORE USE
        const renumberAllLines = (lines) => {
          const counters = {}; // Track counters per indent level

          return lines.map((line) => {
            const match = line.match(/^(\s*)(?:\d+\.|[a-z]+\.|[ivx]+\.)\s+(.*)/);
            if (!match) return line;

            const indent = match[1];
            const content = match[2];
            const level = getIndentLevel(indent);

            // Reset deeper level counters when we return to a shallower level
            Object.keys(counters).forEach((key) => {
              if (parseInt(key) > level) {
                delete counters[key];
              }
            });

            // Initialize or increment counter for this level (always increment, even for empty content)
            if (!counters[level]) counters[level] = 0;
            counters[level]++;

            // Generate prefix based on level
            let prefix;
            if (level === 0) {
              prefix = `${counters[level]}. `;
            } else if (level === 1) {
              prefix = `${numberToLetter(counters[level])}. `;
            } else if (level === 2) {
              prefix = `${numberToRoman(counters[level])}. `;
            } else {
              prefix = `${counters[level]}. `;
            }

            return indent + prefix + content;
          });
        };

        // TAB key - Create sub-bullet/sub-number
        if (e.keyCode === KeyCodeTypeEnum.TAB && !e.shiftKey) {
          e.preventDefault();

          if (this.activeListType === TextListTypeEnum.BULLET) {
            const { lineIdx } = selectionToHardPos();
            const lines = hardLines();
            const currentLine = lines[lineIdx] || '';

            // Match any bullet symbol
            if (/^\s*[•◦▪]\s+/.test(currentLine)) {
              const match = currentLine.match(/^(\s*)[•◦▪]\s+(.*)/);
              if (match) {
                const currentIndent = match[1];
                const content = match[2];
                const newIndent = currentIndent + '    ';
                const level = getIndentLevel(newIndent);
                const newBullet = getBulletSymbol(level);

                lines[lineIdx] = `${newIndent}${newBullet} ${content}`;
                this.text = lines.join('\n');

                const newCaretPos = lineStartIndex(lines, lineIdx) + newIndent.length + 2;
                this.exitEditing();
                this.enterEditing();
                this.setSelectionStart(newCaretPos);
                this.setSelectionEnd(newCaretPos);
              }
            }
          } else if (this.activeListType === TextListTypeEnum.NUMERED) {
            const { lineIdx } = selectionToHardPos();
            const lines = hardLines();
            const currentLine = lines[lineIdx] || '';

            if (/^\s*(?:\d+\.|[a-z]+\.|[ivx]+\.)\s+/.test(currentLine)) {
              const match = currentLine.match(/^(\s*)(?:\d+\.|[a-z]+\.|[ivx]+\.)\s+(.*)/);
              if (match) {
                const currentIndent = match[1];
                const content = match[2];
                const newIndent = currentIndent + '    ';
                const level = getIndentLevel(newIndent);

                // Determine the numbering style based on indent level
                let newPrefix;
                if (level === 1) {
                  newPrefix = 'a. '; // Alphabetic for first sub-level
                } else if (level === 2) {
                  newPrefix = 'i. '; // Roman for second sub-level
                } else {
                  newPrefix = '1. '; // Numbers for deeper levels
                }

                lines[lineIdx] = newIndent + newPrefix + content;

                // Renumber all lines
                const renumbered = renumberAllLines(lines);
                this.text = renumbered.join('\n');

                const newCaretPos =
                  lineStartIndex(renumbered, lineIdx) + newIndent.length + newPrefix.length;
                this.exitEditing();
                this.enterEditing();
                this.setSelectionStart(newCaretPos);
                this.setSelectionEnd(newCaretPos);
              }
            }
          }
        }

        // SHIFT + TAB - Remove indentation
        if (e.keyCode === KeyCodeTypeEnum.TAB && e.shiftKey) {
          e.preventDefault();

          if (this.activeListType === TextListTypeEnum.BULLET) {
            const { lineIdx } = selectionToHardPos();
            const lines = hardLines();
            const currentLine = lines[lineIdx] || '';

            // Match any bullet symbol with indentation
            if (/^\s{4,}[•◦▪]\s+/.test(currentLine)) {
              const match = currentLine.match(/^(\s*)[•◦▪]\s+(.*)/);
              if (match && match[1].length >= 4) {
                const newIndent = match[1].substring(4);
                const content = match[2];
                const level = getIndentLevel(newIndent);
                const newBullet = getBulletSymbol(level);

                lines[lineIdx] = `${newIndent}${newBullet} ${content}`;
                this.text = lines.join('\n');

                const newCaretPos = lineStartIndex(lines, lineIdx) + newIndent.length + 2;
                this.exitEditing();
                this.enterEditing();
                this.setSelectionStart(newCaretPos);
                this.setSelectionEnd(newCaretPos);
              }
            }
          } else if (this.activeListType === TextListTypeEnum.NUMERED) {
            const { lineIdx } = selectionToHardPos();
            const lines = hardLines();
            const currentLine = lines[lineIdx] || '';

            if (/^\s{4,}(?:\d+\.|[a-z]+\.|[ivx]+\.)\s+/.test(currentLine)) {
              const match = currentLine.match(/^(\s*)(?:\d+\.|[a-z]+\.|[ivx]+\.)\s+(.*)/);
              if (match && match[1].length >= 4) {
                const newIndent = match[1].substring(4);
                const content = match[2];
                const level = getIndentLevel(newIndent);

                // Determine the numbering style based on new indent level
                let newPrefix;
                if (level === 0) {
                  newPrefix = '1. ';
                } else if (level === 1) {
                  newPrefix = 'a. ';
                } else if (level === 2) {
                  newPrefix = 'i. ';
                } else {
                  newPrefix = '1. ';
                }

                lines[lineIdx] = newIndent + newPrefix + content;

                // Renumber all lines
                const renumbered = renumberAllLines(lines);
                this.text = renumbered.join('\n');

                const newCaretPos =
                  lineStartIndex(renumbered, lineIdx) + newIndent.length + newPrefix.length;
                this.exitEditing();
                this.enterEditing();
                this.setSelectionStart(newCaretPos);
                this.setSelectionEnd(newCaretPos);
              }
            }
          }
        }

        if (e.keyCode === KeyCodeTypeEnum.ENTER) {
          if (this.activeListType == TextListTypeEnum.BULLET) {
            const { lineIdx } = selectionToHardPos();
            const lines = hardLines();
            const prevLine = lines[lineIdx - 1] || '';

            const indentMatch = prevLine.match(/^(\s*)/);
            const indent = indentMatch ? indentMatch[1] : '';
            const level = getIndentLevel(indent);
            const bullet = getBulletSymbol(level);

            const sel = this.selectionStart || 0;
            this.insertChars(`${indent}${bullet} `, null, this.selectionStart);
            this.exitEditing();
            this.enterEditing();
            this.setSelectionStart(sel + indent.length + 2);
            this.setSelectionEnd(sel + indent.length + 2);
          } else if (this.activeListType == TextListTypeEnum.NUMERED) {
            const { lineIdx } = selectionToHardPos();
            const lines = hardLines();
            const prevLine = lines[lineIdx - 1] || '';

            // Get the indent from previous line
            const indentMatch = prevLine.match(/^(\s*)/);
            const indent = indentMatch ? indentMatch[1] : '';
            const level = getIndentLevel(indent);

            // Determine the numbering style based on indent level
            let newPrefix;
            if (level === 0) {
              newPrefix = '1. '; // Placeholder, will be renumbered
            } else if (level === 1) {
              newPrefix = 'a. '; // Placeholder, will be renumbered
            } else if (level === 2) {
              newPrefix = 'i. '; // Placeholder, will be renumbered
            } else {
              newPrefix = '1. '; // Placeholder, will be renumbered
            }

            const sel = this.selectionStart || 0;
            this.insertChars(`${indent}${newPrefix}`, null, this.selectionStart);

            // Get updated lines and renumber them
            const updatedLines = hardLines();
            const renumbered = renumberAllLines(updatedLines);
            this.text = renumbered.join('\n');

            // Calculate new caret position in renumbered text
            const actualPrefix = renumbered[lineIdx].match(/^(\s*)(?:\d+\.|[a-z]+\.|[ivx]+\.)\s/);
            const prefixLength = actualPrefix
              ? actualPrefix[0].length
              : newPrefix.length + indent.length;
            const newCaretPos = lineStartIndex(renumbered, lineIdx) + prefixLength;

            this.exitEditing();
            this.enterEditing();
            this.setSelectionStart(newCaretPos);
            this.setSelectionEnd(newCaretPos);
          }
        }

        if ([KeyCodeTypeEnum.BACKSPACE, KeyCodeTypeEnum.DELETE].includes(e.keyCode)) {
          if (this.activeListType === TextListTypeEnum.BULLET) {
            const { lineIdx, charIdx } = selectionToHardPos();
            const lines = hardLines();
            if (lineIdx <= 0) return;

            const currentLine = lines[lineIdx] || '';
            const indentMatch = currentLine.match(/^(\s*)/);
            const indent = indentMatch ? indentMatch[1] : '';
            const prefixLen = indent.length + 2; // indent + bullet + space

            if (charIdx < prefixLen) {
              const prev = lines[lineIdx - 1] || '';
              const lineDeletedText = currentLine.replace(/^\s*[•◦▪]\s*/, '');

              lines.splice(lineIdx, 1);
              lines[lineIdx - 1] = `${prev}${prev && lineDeletedText ? ' ' : ''}${lineDeletedText}`;

              this.text = lines.join('\n');

              const caretPos =
                lineStartIndex(lines, lineIdx - 1) +
                lines[lineIdx - 1].length -
                lineDeletedText.length;
              this.exitEditing();
              this.enterEditing();
              this.setSelectionStart(caretPos);
              this.setSelectionEnd(caretPos);
            }
          }

          if (this.activeListType === TextListTypeEnum.NUMERED) {
            const { lineIdx, charIdx } = selectionToHardPos();
            const lines = hardLines();
            if (lineIdx <= 0) return;

            const currentLine = lines[lineIdx] || '';
            const match = currentLine.match(/^(\s*)(?:\d+\.|[a-z]+\.|[ivx]+\.)\s*/);
            const prefixLen = match ? match[0].length : 0;

            if (charIdx < prefixLen) {
              const prev = lines[lineIdx - 1] || '';
              const lineDeletedText = currentLine.replace(/^\s*(?:\d+\.|[a-z]+\.|[ivx]+\.)\s*/, '');

              lines.splice(lineIdx, 1);
              lines[lineIdx - 1] = `${prev}${prev && lineDeletedText ? ' ' : ''}${lineDeletedText}`;

              const renumbered = renumberAllLines(lines);
              this.text = renumbered.join('\n');

              const caretPos =
                lineStartIndex(renumbered, lineIdx - 1) +
                renumbered[lineIdx - 1].length -
                lineDeletedText.length -
                (prev && lineDeletedText ? 1 : 0);

              this.exitEditing();
              this.enterEditing();
              this.setSelectionStart(caretPos);
              this.setSelectionEnd(caretPos);
            }
          }
        }

        if ((e.ctrlKey || e.metaKey) && e.keyCode == KeyCodeTypeEnum.V) {
          if (this.getScaledHeight() > 800) {
            this.canvas.discardActiveObject();
            this.canvas.calcOffset();
            this.canvas.requestRenderAll();
            this.canvas.setActiveObject(this);
          }
        }
      }, 100);
    };
  })(fabric.IText.prototype.onKeyDown);

  fabric.IText.fromObject = function (object, callback) {
    if (!object.text) {
      object.text = '';
    }

    if (typeof object.scaleX === 'undefined') object.scaleX = 1;
    if (typeof object.scaleY === 'undefined') object.scaleY = 1;

    // Ensure fontSize exists for text objects
    if (!object.fontSize) {
      object.fontSize = 18;
    }

    if (!object.fontFamily) {
      object.fontFamily = 'Times New Roman';
    }

    // Handle styles conversion - check if styles is an array or object
    var styles;
    if (Array.isArray(object.styles)) {
      styles = fabric.util.stylesFromArray(object.styles, object.text);
    } else if (object.styles && typeof object.styles === 'object') {
      styles = object.styles;
    } else {
      styles = {};
    }

    // Copy object to prevent mutation
    var objCopy = Object.assign({}, object, { styles: styles });

    // Parse text decorations
    parseDecoration(objCopy);

    // Handle nested styles if they exist
    if (objCopy.styles && typeof objCopy.styles === 'object') {
      for (var i in objCopy.styles) {
        if (objCopy.styles[i] && typeof objCopy.styles[i] === 'object') {
          for (var j in objCopy.styles[i]) {
            if (objCopy.styles[i][j]) {
              parseDecoration(objCopy.styles[i][j]);
            }
          }
        }
      }
    }

    // Handle legacy imgType property - map to appropriate text type
    if (objCopy.imgType === 'textbox') {
      // Create as Textbox instead of IText for better compatibility
      fabric.Object._fromObject('Textbox', objCopy, callback, 'text');
    } else {
      fabric.Object._fromObject('IText', objCopy, callback, 'text');
    }
  };

  // Enhanced parseDecoration function to handle more cases
  function parseDecoration(object) {
    if (object.textDecoration) {
      // Handle both string and array formats
      var decorations = Array.isArray(object.textDecoration)
        ? object.textDecoration
        : object.textDecoration.split(' ');

      decorations.forEach(function (decoration) {
        switch (decoration.toLowerCase()) {
          case 'underline':
            object.underline = true;
            break;
          case 'line-through':
          case 'linethrough':
            object.linethrough = true;
            break;
          case 'overline':
            object.overline = true;
            break;
        }
      });

      delete object.textDecoration;
    }

    // Handle legacy boolean properties that might be strings
    if (typeof object.underline === 'string') {
      object.underline = object.underline === 'true';
    }
    if (typeof object.linethrough === 'string') {
      object.linethrough = object.linethrough === 'true';
    }
    if (typeof object.overline === 'string') {
      object.overline = object.overline === 'true';
    }
  }

  // Enhanced stylesToArray function with better error handling
  fabric.util.stylesToArray = function (styles, text) {
    if (!styles || !text) return [];

    // Clone style structure to prevent mutation
    var clonedStyles = fabric.util.object.clone(styles, true);
    var textLines = text.split('\n');
    var charIndex = -1;
    var prevStyle = {};
    var stylesArray = [];

    // Loop through each textLine
    for (var i = 0; i < textLines.length; i++) {
      if (!clonedStyles || !clonedStyles[i]) {
        // No styles exist for this line, so add the line's length to the charIndex total
        charIndex += textLines[i].length;
        // Don't forget to account for newline characters (except for the last line)
        if (i < textLines.length - 1) {
          charIndex += 1;
        }
        continue;
      }

      // Loop through each character of the current line
      for (var c = 0; c < textLines[i].length; c++) {
        charIndex++;
        var thisStyle = clonedStyles[i][c];

        // Check if style exists for this character
        if (thisStyle && Object.keys(thisStyle).length > 0) {
          var styleChanged = fabric.util.hasStyleChanged(prevStyle, thisStyle, true);
          if (styleChanged) {
            stylesArray.push({
              start: charIndex,
              end: charIndex + 1,
              style: thisStyle,
            });
          } else {
            // If style is the same as previous character, increase end index
            if (stylesArray.length > 0) {
              stylesArray[stylesArray.length - 1].end++;
            }
          }
        }
        prevStyle = thisStyle || {};
      }

      // Account for newline character (except for the last line)
      if (i < textLines.length - 1) {
        charIndex += 1;
      }
    }

    return stylesArray;
  };

  function parseDecoration(object) {
    if (object.textDecoration) {
      object.textDecoration.indexOf('underline') > -1 && (object.underline = true);
      object.textDecoration.indexOf('line-through') > -1 && (object.linethrough = true);
      object.textDecoration.indexOf('overline') > -1 && (object.overline = true);
      delete object.textDecoration;
    }
  }
}

function setupImageObject() {
  fabric.MTGImage = fabric.util.createClass(fabric.Image, {
    type: 'MTGImage',
    crossOrigin: 'anonymous',
    async: true,
    // globalCompositeOperation: 'source-atop',
    perPixelTargetFind: true,
    targetFindTolerance: 4,
    initialize: function (element, options) {
      options = options || {};

      this.callSuper('initialize', element, options);

      this.set('imgType', options.imgType || '');
      this.set('imgUrl', options.imgUrl || '');
      this.set('imgId', options.imgId || '');
      this.set('hasColor', options.hasColor || '');
      this.set('tags', options.tags || '');
      this.set('chartType', options.chartType || '');
      this.set('chartData', options.chartData || '');
    },
    toObject: function () {
      return fabric.util.object.extend(this.callSuper('toObject'), {
        imgType: this.imgType,
        imgUrl: this.imgUrl,
        imgId: this.imgId,
        hasColor: this.hasColor,
        tags: this.tags,
        clipPath: this.clipPath,
        chartType: this.chartType,
        chartData: this.chartData,
      });
    },
  });

  fabric.MTGImage.fromObject = function (object, callback) {
    fabric.util.loadImage(
      object.src,
      function (img) {
        fabric.MTGImage.prototype._initFilters.call(object, object.filters, function (filters) {
          object.filters = filters || [];
          fabric.MTGImage.prototype._initFilters.call(
            object,
            [object.resizeFilter],
            function (resizeFilters) {
              object.resizeFilter = resizeFilters[0];
              fabric.util.enlivenObjectEnlivables(object, object, function () {
                var image = new fabric.MTGImage(img, object);
                callback && callback(image, false);
              });
            },
          );
        });
      },
      null,
      'anonymous',
    );
  };

  fabric.MTGImage.fromURL = function (url, callback, options) {
    fabric.util.loadImage(
      url,
      function (img) {
        callback(new fabric.MTGImage(img, options));
      },
      null,
      'anonymous',
    );
  };

  // extend legacy templates objects
}

function setupLineObject() {
  fabric.DynamicLine = fabric.util.createClass(fabric.Path, {
    type: 'DynamicLine',
    initialize: function (element, options) {
      options || (options = {});
      this.callSuper('initialize', element, options);

      this.set('leftPointer', options.leftPointer || null);
      this.set('rightPointer', options.rightPointer || null);
      this.set('objectCaching', false);
    },

    toObject: function () {
      return fabric.util.object.extend(this.callSuper('toObject'), {
        leftPointer: this.leftPointer,
        rightPointer: this.rightPointer,
        perPixelTargetFind: true,
        targetFindTolerance: 4,
        padding: 0,
        hasControls: false,
        hasBorders: false,
        pathOffset: {
          x: 0,
          y: 0,
        },
      });
    },
    _render: function (ctx) {
      ctx.save();

      const last = this.path[this.path.length - 1];
      const x1 = this.path[0][1];
      const y1 = this.path[0][2];
      let x2, y2;
      let xDiff, yDiff;

      if (this.path.length === 2 && this.path[1].length === 3) {
        // straight line
        x2 = this.path[1][1];
        y2 = this.path[1][2];
        xDiff = x2 - x1;
        yDiff = y2 - y1;
      } else {
        // For curved paths (quadratic or cubic)
        if (last[0] === 'Q') {
          // For quadratic curves, use the end point
          x2 = last[3];
          y2 = last[4];
          // Use control point for angle calculation
          xDiff = x2 - last[1];
          yDiff = y2 - last[2];
        } else {
          x2 = last[last.length - 2];
          y2 = last[last.length - 1];
          xDiff = x2 - this.path[1][1];
          yDiff = y2 - this.path[1][2];
        }
      }

      let angle = Math.atan2(yDiff, xDiff);
      ctx.translate(x2, y2);
      ctx.rotate(angle);
      ctx.beginPath();
      if (this.rightPointer && this.rightPointer !== 'remove')
        drawPointer(ctx, this.rightPointer, this.stroke, this.strokeWidth);

      ctx.fill();
      ctx.restore();
      ctx.save();

      ctx.translate(x1, y1);
      if (this.path[1][0] === 'Q') {
        // For quadratic curves, calculate angle from start point to control point
        xDiff = this.path[1][1] - x1;
        yDiff = this.path[1][2] - y1;
      } else {
        xDiff = this.path[1][1] - x1;
        yDiff = this.path[1][2] - y1;
      }
      angle = Math.atan2(yDiff, xDiff) + Math.PI;
      ctx.rotate(angle);
      ctx.beginPath();

      if (this.leftPointer && this.leftPointer !== 'remove')
        drawPointer(ctx, this.leftPointer, this.stroke, this.strokeWidth);

      ctx.closePath();
      ctx.fill();
      ctx.restore();

      this.callSuper('_render', ctx);
    },
  });

  fabric.DynamicLine.fromObject = function (object, callback) {
    if (typeof object.sourcePath === 'string') {
      var pathUrl = object.sourcePath;
      fabric.loadSVGFromURL(pathUrl, function (elements) {
        var path = elements[0];
        path.setOptions(object);
        callback && callback(path);
      });
    } else {
      fabric.Object._fromObject('DynamicLine', object, callback, 'path');
    }
  };

  fabric.DynamicLine.async = true;
}

function drawPointer(ctx, pointer, stroke, strokeWidth) {
  const x = strokeWidth / 4;
  switch (pointer) {
    case 'arrow':
      ctx.moveTo(15 * x, 0);
      ctx.lineTo(0 * x, 15 * x);
      ctx.lineTo(0 * x, -15 * x);
      ctx.closePath();
      ctx.fillStyle = stroke;
      break;
    case 'circle':
      ctx.arc(10 * x - 0.3, 0, 10 * x, 0, 2 * Math.PI, false);
      ctx.fillStyle = stroke;
      break;
    case 'square':
      ctx.rect(0 * x, -10 * x, 20 * x, 20 * x);
      ctx.fillStyle = stroke;
      break;
    case 'line':
      ctx.moveTo(0, -10 * x);
      ctx.lineTo(0, 10 * x);
      ctx.strokeStyle = stroke;
      ctx.stroke();
      break;
    case 'remove':
      break;
    default:
      break;
  }
}

function setupUtilsFunctions() {
  fabric.Canvas.prototype.getItemByName = function (name, multiple) {
    let object = null,
      objects = this.getObjects();

    if (multiple) {
      object = [];
    }

    for (let i = 0, len = this.size(); i < len; i++) {
      if (objects[i].name && objects[i].name === name) {
        if (multiple) {
          object.push(objects[i]);
        } else {
          object = objects[i];
          break;
        }
      }
    }

    return object;
  };
}

function setupDynamicShapes() {
  fabric.Object.prototype.toObject = (function (toObject) {
    return function () {
      return fabric.util.object.extend(toObject.call(this), {
        imgType: this.imgType,
        originalSvgId: this.originalSvgId,
      });
    };
  })(fabric.Object.prototype.toObject);

  // Ellipse props
  fabric.Ellipse.prototype.toObject = (function (toObject) {
    return function () {
      return fabric.util.object.extend(toObject.call(this), {
        rx: this.rx,
        ry: this.ry,
      });
    };
  })(fabric.Ellipse.prototype.toObject);

  // Circle props
  fabric.Circle.prototype.toObject = (function (toObject) {
    return function () {
      return fabric.util.object.extend(toObject.call(this), {
        radius: this.radius,
        controlPoint: this.controlPoint,
      });
    };
  })(fabric.Circle.prototype.toObject);
}

export function extendControls() {
  const unlock =
    "data:image/svg+xml,%3Csvg data-name='Componente 147 – 1' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 19 19'%3E%3Ccircle data-name='Elipse 249' cx='9.5' cy='9.5' r='9.5' fill='%239e9e9e'/%3E%3Cg data-name='Componente 148 – 1' transform='translate(5.6 3.8)' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.2'%3E%3Crect data-name='Retângulo 3011' width='7.7' height='5.6' rx='1.4' transform='translate(0 4.9)' fill='%23fff' stroke='%23fff'/%3E%3Cpath data-name='Caminho 1164' d='M1.7 4.9V2A2.1 2.1 0 0 1 4 0h0A2.1 2.1 0 0 1 6 2.1V5' fill='none' stroke='%23fff'/%3E%3Cpath data-name='Linha 99' fill='none' stroke='%239e9e9e' d='M3.9 7.2v1.4'/%3E%3C/g%3E%3C/svg%3E";

  const img = document.createElement('img');
  img.src = unlock;

  fabric.Object.prototype.controls.lockControl = new fabric.Control({
    x: -0.5,
    y: -0.5,
    cursorStyle: 'pointer',
    mouseUpHandler: unlockObject,
    render: renderIcon,
    cornerSize: 24,
    visible: false,
  });
  function unlockObject(eventData, transform) {
    const target = transform.target;
    target.set({
      lockMovementX: false,
      lockMovementY: false,
      lockScalingX: false,
      lockScalingY: false,
      lockUniScaling: false,
      lockRotation: false,
      editable: true,
      borderColor: '#63A8A2',
      cornerColor: '#63A8A2',
    });

    target.setControlsVisibility({
      mtr: true,
      lockControl: false,
    });
    instance.requestRenderAll();
  }
  function renderIcon(ctx, left, top, styleOverride, fabricObject) {
    const size = this.cornerSize;
    ctx.save();
    ctx.translate(left, top);
    ctx.rotate(fabric.util.degreesToRadians(fabricObject.angle));
    ctx.drawImage(img, -size / 2, -size / 2, size, size);
    ctx.restore();
  }
}

function setupCustomTextLabel() {
  fabric.CustomTextLabel = fabric.util.createClass(fabric.Textbox, {
    type: 'CustomTextLabel',

    initialize: function (text, options) {
      options = options || {};
      this.callSuper('initialize', text, options);

      // Add any custom properties here
      this.set('object_id', options.object_id || '');
      this.set('labelType', options.labelType || '');
      this.set('tag', options.tag || '');
    },

    toObject: function () {
      return fabric.util.object.extend(this.callSuper('toObject'), {
        object_id: this.object_id,
        labelType: this.labelType,
        tag: this.tag,
      });
    },
  });

  // Deserialization (from JSON)
  fabric.CustomTextLabel.fromObject = function (object, callback) {
    return callback(new fabric.CustomTextLabel(object.text, object));
  };
}

export function initCustomMethods() {
  extendMainObject();
  setupImageObject();
  setupLineObject();
  setupTextObject();
  setupUtilsFunctions();
  setupDynamicShapes();
  setupCustomTextLabel();
}
